import { parseChart, sampleChart } from './chart';
export { CHART_KINDS } from './chart';
export const CONTENT_KEY = 'content';
export const MAP_LINK_KEY = 'mapLink';
export const CONTENT_WIDTHS = {
    small: 140,
    medium: 200,
    large: 280,
};
/**
 * The bounds, stated once.
 *
 * A node's metadata travels in every sync and every undo step, so it has to stay small: a table
 * of forty rows is a spreadsheet's job, and a drawing of six thousand points is already a
 * detailed sketch.
 */
export const CONTENT_LIMITS = {
    tableRows: 40,
    tableColumns: 12,
    cellLength: 240,
    chartPoints: 24,
    chartLabelLength: 40,
    strokes: 400,
    drawingPoints: 6_000,
    drawingSide: 2_000,
    titleLength: 200,
    urlLength: 2_000,
};
const HEX = /^#[0-9a-fA-F]{6}$/;
const SIZES = ['small', 'medium', 'large'];
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
function sizeOf(value) {
    return SIZES.includes(value) ? value : 'medium';
}
function aspectOf(value, fallback) {
    const aspect = finite(value);
    // Between a tall phone screenshot and a panorama. Anything outside is a corrupt value.
    return aspect === undefined || aspect <= 0 ? fallback : Math.min(4, Math.max(0.25, aspect));
}
function idOf(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 128 ? value : undefined;
}
function durationOf(value) {
    const duration = finite(value);
    return duration === undefined || duration < 0 ? {} : { durationMs: Math.round(duration) };
}
/** Only web addresses: a `javascript:` link in a shared map must not become a button. */
export function safeUrl(value) {
    if (typeof value !== 'string')
        return undefined;
    const url = value.trim();
    if (url.length === 0 || url.length > CONTENT_LIMITS.urlLength)
        return undefined;
    return /^https?:\/\/[^\s]+$/i.test(url) ? url : undefined;
}
function tableOf(value) {
    if (!Array.isArray(value.rows))
        return undefined;
    const rows = [];
    let columns = 0;
    for (const row of value.rows.slice(0, CONTENT_LIMITS.tableRows)) {
        if (!Array.isArray(row))
            return undefined;
        const cells = row
            .slice(0, CONTENT_LIMITS.tableColumns)
            .map((cell) => typeof cell === 'string'
            ? cell.slice(0, CONTENT_LIMITS.cellLength)
            : typeof cell === 'number'
                ? String(cell)
                : '');
        columns = Math.max(columns, cells.length);
        rows.push(cells);
    }
    if (rows.length === 0 || columns === 0)
        return undefined;
    // Ragged rows are padded: every renderer and the editor can then assume a rectangle.
    return {
        kind: 'table',
        rows: rows.map((row) => [...row, ...Array(columns - row.length).fill('')]),
        header: value.header === true,
    };
}
function drawingOf(value) {
    const width = finite(value.width);
    const height = finite(value.height);
    if (width === undefined || height === undefined || width <= 0 || height <= 0)
        return undefined;
    if (!Array.isArray(value.strokes))
        return undefined;
    const side = CONTENT_LIMITS.drawingSide;
    const strokes = [];
    let budget = CONTENT_LIMITS.drawingPoints * 2;
    for (const stroke of value.strokes.slice(0, CONTENT_LIMITS.strokes)) {
        if (budget <= 0)
            break;
        if (!isRecord(stroke) || !Array.isArray(stroke.points))
            continue;
        const points = [];
        for (const coordinate of stroke.points.slice(0, budget - (budget % 2))) {
            const number = finite(coordinate);
            if (number === undefined)
                break;
            points.push(Math.round(Math.min(side, Math.max(0, number)) * 10) / 10);
        }
        if (points.length % 2 === 1)
            points.pop();
        if (points.length < 2)
            continue;
        budget -= points.length;
        strokes.push({
            color: typeof stroke.color === 'string' && HEX.test(stroke.color)
                ? stroke.color
                : '#1f2937',
            width: Math.min(24, Math.max(1, finite(stroke.width) ?? 3)),
            points,
        });
    }
    return {
        kind: 'drawing',
        width: Math.min(side, width),
        height: Math.min(side, height),
        strokes,
        size: sizeOf(value.size),
    };
}
/** The content a metadata value describes, clamped to the limits — or nothing, if it is not one. */
export function parseContent(value) {
    if (!isRecord(value))
        return undefined;
    switch (value.kind) {
        case 'image': {
            const attachmentId = idOf(value.attachmentId);
            if (attachmentId === undefined)
                return undefined;
            return {
                kind: 'image',
                attachmentId: attachmentId,
                aspect: aspectOf(value.aspect, 4 / 3),
                size: sizeOf(value.size),
            };
        }
        case 'video': {
            const attachmentId = idOf(value.attachmentId);
            const url = safeUrl(value.url);
            if (attachmentId === undefined && url === undefined)
                return undefined;
            return {
                kind: 'video',
                ...(attachmentId === undefined ? {} : { attachmentId: attachmentId }),
                ...(url === undefined ? {} : { url }),
                aspect: aspectOf(value.aspect, 16 / 9),
                size: sizeOf(value.size),
                ...durationOf(value.durationMs),
            };
        }
        case 'audio': {
            const attachmentId = idOf(value.attachmentId);
            if (attachmentId === undefined)
                return undefined;
            return {
                kind: 'audio',
                attachmentId: attachmentId,
                ...durationOf(value.durationMs),
                ...(value.recorded === true ? { recorded: true } : {}),
            };
        }
        case 'table':
            return tableOf(value);
        case 'chart':
            return parseChart(value);
        case 'drawing':
            return drawingOf(value);
        default:
            return undefined;
    }
}
export function parseMapLink(value) {
    if (!isRecord(value))
        return undefined;
    const mapId = idOf(value.mapId);
    if (mapId === undefined)
        return undefined;
    return {
        mapId: mapId,
        title: typeof value.title === 'string' ? value.title.slice(0, CONTENT_LIMITS.titleLength) : '',
    };
}
/** What a node shows besides its text, if anything. */
export function contentOf(node) {
    return parseContent(node.metadata?.[CONTENT_KEY]);
}
/** The map a node leads to, if it leads anywhere. */
export function mapLinkOf(node) {
    return parseMapLink(node.metadata?.[MAP_LINK_KEY]);
}
/**
 * The node's metadata with one key set or removed, everything else kept.
 *
 * `undefined` when nothing is left, so the caller clears the field instead of storing `{}` — an
 * empty record and no record mean the same thing, and only one of them should be in a document.
 */
function withKey(metadata, key, value) {
    const next = Object.fromEntries(Object.entries(metadata ?? {}).filter(([name]) => name !== key));
    if (value !== null)
        next[key] = value;
    return Object.keys(next).length === 0 ? undefined : next;
}
export function withContent(metadata, content) {
    return withKey(metadata, CONTENT_KEY, content);
}
export function withMapLink(metadata, link) {
    return withKey(metadata, MAP_LINK_KEY, link);
}
/** The attachments a node's content points at, so a file in use is not offered for removal. */
export function contentAttachments(content) {
    if (content === undefined)
        return [];
    if (content.kind === 'image' || content.kind === 'audio')
        return [content.attachmentId];
    if (content.kind === 'video' && content.attachmentId !== undefined)
        return [content.attachmentId];
    return [];
}
/** A table to start from: three columns, a header and two rows, so there is something to edit. */
export function starterTable(header = ['Item', 'Qtd.', 'Valor']) {
    return {
        kind: 'table',
        header: true,
        rows: [[...header], ['', '', ''], ['', '', '']],
    };
}
/** A chart to start from: a sample that shows the kind at its best. */
export function starterChart(chart = 'column', portuguese = true) {
    return sampleChart(chart, portuguese);
}
//# sourceMappingURL=content.js.map